import { useEffect, useMemo } from 'react';

import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Flex } from '@chakra-ui/react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useSidebarCohortViews } from 'components/RouteMatchSidebarNavContainer/hooks/dataHooks';
import { useFilteredEmployeeCount } from 'components/Views/Hooks/dataHooks';
import { useSidebarNavMatchRoute } from 'components/hooks/useSidebarNavMatchRoute';
import { useDispatch, useSelector } from 'react-redux';
import {
  setIsViewStatic,
  setViewName,
  setViewVisibility,
  useViewName,
  useViewsQuery,
  useIsViewStatic,
  useViewVisibility,
  useCheckedEmployees,
  useCheckedBaseCohort,
  useSaveViewErrorMessage,
  useViewsPanelState,
} from 'reducers/viewsSlice';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { NeedAttention } from 'assets/assetsV1/iconComponents';

import {
  VIEWS_DYNAMIC_TYPES_LABELS,
  VIEWS_DYNAMIC_TYPES_VALUES,
  VIEWS_VISIBILITY_TYPES_LABELS,
  VIEWS_VISIBILITY_TYPES_VALUES,
} from 'constants/ProjectConstants';

import InputLabel from 'uiToolkitV1/InputLabel';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { SelectV2 } from 'uiToolkitV1/SelectV2';
import { GenericControlAsButton } from 'uiToolkitV1/SelectV2/GenericControl';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { FilterContent } from '../FilterContent/FilterContent';
import { FilterSidebar } from '../FilterSidebar/FilterSidebar';
import { FiltersTagList } from '../FiltersTagList';

const StyledScrollableBox = styled(Box)`
  overflow: auto;
  // max-height: 70vh;
`;

const StyledAccordion = styled(Accordion)`
  border: 1px solid ${(props) => props.theme.colors.border.EBEFF3} !important;
  border-radius: 8px;
  margin-top: 24px;

  & .chakra-accordion__button {
    font-weight: 590;
    font-size: 14px;
  }
  & .chakra-collapse {
    border-top: 1px solid ${(props) => props.theme.colors.border.EBEFF3};
  }
`;

const ApplyFiltersBody = ({ viewAction }) => {
  const theme = useTheme();

  return (
    <>
      <StyledScrollableBox flex={1} borderRadius="8px 0 0 0" borderRight={`4px solid ${theme.colors.neutral.EBEFF3}`}>
        <FilterSidebar viewAction={viewAction} />
      </StyledScrollableBox>
      <StyledScrollableBox flex={3} borderRadius="0 8px 0 0">
        <FilterContent />
      </StyledScrollableBox>
    </>
  );
};

const SavePageBody = () => {
  const viewName = useViewName();
  const viewVisibility = useViewVisibility();
  const isViewStatic = useIsViewStatic();
  const selectedEmployees = useCheckedEmployees();
  const isEmployeesSelected = selectedEmployees?.length > 0;
  const filters = useViewsQuery();
  const dispatch = useDispatch();
  const saveViewErrorMessage = useSaveViewErrorMessage();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const { data: filteredEmployeeCount } = useFilteredEmployeeCount();
  const baseCohort = useCheckedBaseCohort();

  const { data: { isIc } = {}, isLoading, isSuccess } = useSidebarCohortViews(useSidebarNavMatchRoute());
  const { constraints = {} } = useViewsPanelState();

  // + 1 for base Cohort
  const totalFilters = Object.keys(filters).length + 1;

  const disallowPublic = baseCohort.uuid !== '-1';
  const disallowPrivate = constraints?.disallowPrivate;
  const source = constraints?.source;

  const visibilityOptions = [
    {
      label: VIEWS_VISIBILITY_TYPES_LABELS.PRIVATE,
      value: VIEWS_VISIBILITY_TYPES_VALUES.PRIVATE,
    },
    {
      label: VIEWS_VISIBILITY_TYPES_LABELS.SHARED,
      value: VIEWS_VISIBILITY_TYPES_VALUES.SHARED,
    },
  ];

  useEffect(() => {
    if (constraints?.disallowPrivate && viewVisibility === VIEWS_VISIBILITY_TYPES_VALUES.PRIVATE) {
      dispatch(setViewVisibility(VIEWS_VISIBILITY_TYPES_VALUES.SHARED));
    }
  }, [constraints?.disallowPrivate, dispatch, viewVisibility]);

  const allowedVisibilityOptions = useMemo(() => visibilityOptions, [visibilityOptions]);

  const viewTypeOptions = [
    {
      label: VIEWS_DYNAMIC_TYPES_LABELS.DYNAMIC,
      value: VIEWS_DYNAMIC_TYPES_VALUES.DYNAMIC,
    },
    {
      label: VIEWS_DYNAMIC_TYPES_LABELS.STATIC,
      value: VIEWS_DYNAMIC_TYPES_VALUES.STATIC,
    },
  ];

  const selectedVisibility = allowedVisibilityOptions.find((option) => option.value === viewVisibility);
  let selectedViewType = isViewStatic === 0 ? viewTypeOptions[0] : viewTypeOptions[1];

  // override to view type to static if any Employee name is selected
  if (isEmployeesSelected) {
    selectedViewType = viewTypeOptions[1];
  }

  const visibilityTooltipText = disallowPublic
    ? "Shared views can only be created with 'My Company' as base"
    : source === 'REVIEW'
      ? 'Only Shared View can be used to create Review Cycle'
      : 'Only Shared View can be used to create Engagement Survey';

  return (
    <Flex
      p="24px 20px"
      // height={'70vh'}
      width="100%"
      borderRadius="8px 0 0 0"
      flexDir="column"
    >
      <Flex flexGrow={1} overflowY="auto" flexDir="column">
        <Flex width="100%" gap="20px">
          <InputLabel labelText="View Name*" containerProps={{ px: '0px', py: '0px', flex: 1 }}>
            <InputV2
              isDashed
              autoFocus
              fontSize="14px"
              value={viewName}
              onChange={(e) => dispatch(setViewName(e.target.value?.trim() === '' ? '' : e.target.value))}
              maxLength={50}
            />
          </InputLabel>
          {!isIc && (
            <Tooltip label={visibilityTooltipText} isDisabled={!disallowPublic && !disallowPrivate}>
              <Box flex={1}>
                <InputLabel labelText="Visibility" containerProps={{ px: '0px', py: '0px', flex: 1 }}>
                  <SelectV2
                    menuItems={allowedVisibilityOptions}
                    onSelect={({ value }) => dispatch(setViewVisibility(value))}
                    selectedItem={selectedVisibility}
                    menuButtonProps={{
                      disabled: disallowPrivate || disallowPublic,
                    }}
                    renderAs={GenericControlAsButton}
                  />
                </InputLabel>
              </Box>
            </Tooltip>
          )}
          <Tooltip
            label="Since you have chosen static filters exclusively, dynamic views are not applicable in this view"
            isDisabled={!isEmployeesSelected}
          >
            <Box flex={1}>
              <InputLabel labelText="View Type" containerProps={{ px: '0px', py: '0px', flex: 1 }}>
                <SelectV2
                  menuItems={viewTypeOptions}
                  onSelect={({ value }) => dispatch(setIsViewStatic(value))}
                  selectedItem={selectedViewType}
                  menuButtonProps={{
                    disabled: isEmployeesSelected,
                  }}
                  renderAs={GenericControlAsButton}
                />
              </InputLabel>
            </Box>
          </Tooltip>
        </Flex>

        <StyledAccordion
          // allowToggle
          defaultIndex={[0]}
        >
          <AccordionItem>
            <AccordionButton>
              {/* <AccordionIcon /> */}
              <Box as="span" flex="1" textAlign="left">
                User Filters ({totalFilters})
              </Box>
              {filteredEmployeeCount !== null && filteredEmployeeCount !== undefined && (
                <TextBodyMedium bold>
                  {filteredEmployeeCount} Employee{filteredEmployeeCount > 1 ? 's' : ''}
                </TextBodyMedium>
              )}
            </AccordionButton>
            <AccordionPanel p="0 0 4px 0">
              <FiltersTagList showLabel={false} />
            </AccordionPanel>
          </AccordionItem>
        </StyledAccordion>
      </Flex>
      {saveViewErrorMessage && (
        <Flex p={3}>
          <NeedAttention boxSize="20px" color="alerts.negative" />
          <TextBodyMedium ml={1} color="alerts.negative">
            {saveViewErrorMessage}
          </TextBodyMedium>
        </Flex>
      )}
    </Flex>
  );
};

// export const ModalBody = () => {
//   const viewsScreen = useViewSubScreen();

//   // if (viewsScreen === VIEWS_SUB_SCREENS.UPLOAD_CSV) {
//   //   return <UploadCsvBody />;
//   // }

//   if (viewsScreen === VIEWS_SUB_SCREENS.SAVE) {
//     return <SavePageBody />;
//   }
//   {viewsScreen === VIEWS_SUB_SCREENS.SAVE ? <SavePageBody /> : <ApplyFiltersBody />}

//   return <ApplyFiltersBody />;
// };

export { SavePageBody, ApplyFiltersBody };
